<template>
  <BaseModal
    :is-open="isOpen"
    title="Move Player to Different Seat"
    max-width="550px"
    @close="handleCancel"
  >
    <div class="move-player-container">
      <!-- Player Info -->
      <div class="player-info">
        <div class="player-avatar">
          <img
            v-if="playerData?.profile_picture"
            :src="`/images/profiles/${playerData.profile_picture}.png`"
            :alt="playerData.player_name"
            class="avatar-image"
          />
          <div v-else class="avatar-initials">
            {{ getInitials(playerData?.player_name || '?') }}
          </div>
        </div>

        <div class="player-details">
          <h3 class="player-name">{{ playerData?.player_name }}</h3>
          <div class="current-seat">
            <span class="material-symbols-outlined">event_seat</span>
            <span>Currently in Seat {{ (playerData?.current_seat || 0) + 1 }}</span>
            <span class="team-badge">{{ getCurrentTeam }}</span>
          </div>
        </div>
      </div>

      <!-- Seat Selection -->
      <div class="seat-selection">
        <p class="selection-prompt">Select new seat for this player:</p>

        <div class="seats-grid">
          <button
            v-for="seat in availableSeats"
            :key="seat"
            @click="handleSelectSeat(seat)"
            class="seat-option"
            :class="{ selected: selectedSeat === seat }"
          >
            <div class="seat-number">
              <span class="material-symbols-outlined">event_seat</span>
              <span>{{ seat + 1 }}</span>
            </div>
            <div class="seat-team">{{ getTeamLabel(seat) }}</div>
          </button>
        </div>
      </div>

      <!-- Warning -->
      <div class="warning-message">
        <span class="material-symbols-outlined">info</span>
        <span>
          Moving a player will change their team assignment. This may affect game balance.
        </span>
      </div>

      <!-- Action Buttons -->
      <div class="move-actions">
        <button @click="handleCancel" class="btn-cancel">
          <span class="material-symbols-outlined">close</span>
          <span>Cancel</span>
        </button>
        <button
          @click="handleConfirm"
          :disabled="selectedSeat === null || selectedSeat === playerData?.current_seat"
          class="btn-confirm"
        >
          <span class="material-symbols-outlined">swap_horiz</span>
          <span>Move Player</span>
        </button>
      </div>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import BaseModal from './BaseModal.vue'

/**
 * MovePlayerModal Component
 *
 * Allows game creator to move a player to a different seat.
 * Shows available seats and team assignments.
 *
 * Props:
 * - player_id: ID of player to move
 * - player_name: Name of player
 * - profile_picture: Player's avatar
 * - current_seat: Current seat index (0-3)
 * - available_seats: Array of seat indices that are empty
 *
 * Emits:
 * - move: { player_id: string, new_seat: number } - Move confirmed
 * - cancel: [] - Move cancelled
 */

interface Props {
  isOpen: boolean
  data: {
    player_id?: string
    player_name?: string
    profile_picture?: string
    current_seat?: number
    available_seats?: number[]
  } | null
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  data: null,
})

const emit = defineEmits<{
  move: [{ player_id: string; new_seat: number }]
  cancel: []
  close: []
}>()

const selectedSeat = ref<number | null>(null)

/**
 * Player data
 */
const playerData = computed(() => props.data)

/**
 * Available seats (including current seat)
 */
const availableSeats = computed(() => {
  const seats = props.data?.available_seats || []
  const currentSeat = props.data?.current_seat

  // Include all seats except current one
  return [0, 1, 2, 3].filter(
    (seat) => seats.includes(seat) || seat === currentSeat
  )
})

/**
 * Get current team label
 */
const getCurrentTeam = computed(() => {
  const seat = props.data?.current_seat || 0
  return getTeamLabel(seat)
})

/**
 * Get initials from name
 */
function getInitials(name: string): string {
  return name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

/**
 * Get team label for seat
 */
function getTeamLabel(seat: number): string {
  return seat % 2 === 0 ? 'Team A' : 'Team B'
}

/**
 * Handle seat selection
 */
function handleSelectSeat(seat: number): void {
  selectedSeat.value = seat
}

/**
 * Handle confirm
 */
function handleConfirm(): void {
  if (
    selectedSeat.value !== null &&
    selectedSeat.value !== playerData.value?.current_seat &&
    playerData.value?.player_id
  ) {
    emit('move', {
      player_id: playerData.value.player_id,
      new_seat: selectedSeat.value,
    })
    selectedSeat.value = null
  }
}

/**
 * Handle cancel
 */
function handleCancel(): void {
  selectedSeat.value = null
  emit('cancel')
  emit('close')
}
</script>

<style scoped>
.move-player-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 0.5rem;
}

/* Player Info */
.player-info {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 1.25rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.player-avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  border: 3px solid rgba(232, 197, 71, 0.3);
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-initials {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e8c547, #d4af37);
  color: #0d1f2d;
  font-size: 1.5rem;
  font-weight: 700;
}

.player-details {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
}

.player-name {
  font-size: 1.25rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
}

.current-seat {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9375rem;
  color: rgba(255, 255, 255, 0.8);
}

.current-seat .material-symbols-outlined {
  font-size: 1.125rem;
}

.team-badge {
  padding: 0.25rem 0.625rem;
  background: rgba(232, 197, 71, 0.2);
  border-radius: 4px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #e8c547;
}

/* Seat Selection */
.seat-selection {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.selection-prompt {
  font-size: 0.9375rem;
  color: rgba(255, 255, 255, 0.9);
  text-align: center;
  margin: 0;
}

.seats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.75rem;
}

.seat-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 0.75rem;
  border-radius: 10px;
  border: 2px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  cursor: pointer;
  transition: all 0.2s;
}

.seat-option:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(232, 197, 71, 0.4);
  transform: translateY(-2px);
}

.seat-option.selected {
  background: linear-gradient(135deg, rgba(232, 197, 71, 0.2), rgba(212, 175, 55, 0.2));
  border-color: #e8c547;
  box-shadow: 0 0 20px rgba(232, 197, 71, 0.3);
}

.seat-number {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  font-size: 1.125rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
}

.seat-number .material-symbols-outlined {
  font-size: 1.5rem;
  color: #e8c547;
}

.seat-team {
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 600;
}

/* Warning Message */
.warning-message {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(251, 191, 36, 0.1);
  border: 1px solid rgba(251, 191, 36, 0.3);
  border-radius: 8px;
  font-size: 0.875rem;
  color: rgba(251, 191, 36, 0.9);
  line-height: 1.5;
}

.warning-message .material-symbols-outlined {
  font-size: 1.125rem;
  flex-shrink: 0;
  margin-top: 0.125rem;
}

/* Action Buttons */
.move-actions {
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
}

.btn-cancel,
.btn-confirm {
  flex: 1;
  padding: 0.875rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: none;
}

.btn-cancel {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.btn-cancel:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

.btn-confirm {
  background: linear-gradient(135deg, #e8c547 0%, #d4af37 100%);
  color: #0d1f2d;
  box-shadow: 0 4px 12px rgba(232, 197, 71, 0.3);
}

.btn-confirm:hover:not(:disabled) {
  background: linear-gradient(135deg, #f0d060 0%, #e0bf47 100%);
  box-shadow: 0 6px 16px rgba(232, 197, 71, 0.4);
  transform: translateY(-2px);
}

.btn-confirm:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(232, 197, 71, 0.3);
}

.btn-confirm:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.5);
  box-shadow: none;
}

.btn-cancel .material-symbols-outlined,
.btn-confirm .material-symbols-outlined {
  font-size: 1.25rem;
}

/* Responsive */
@media (max-width: 640px) {
  .seats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .move-actions {
    flex-direction: column;
  }
}
</style>
